import React, { useEffect, useContext, useState } from 'react'
import { useMutation, useLazyQuery } from '@apollo/client'
import { useParams, useHistory, Link } from 'react-router-dom'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import FormControl from 'react-bootstrap/FormControl'

import AppContext from '../../context'
import { useAppDispatch } from '../../hooks'
import { GET_USER_TOKEN } from '../../graphql'
import { GET_MAGIC_LINK } from '../../graphql/publicQueries'
import { setCurrentEventId } from '../../features/siteSlice'
import { saveSelectedEvent, useQueryParams } from '../../common'
import { myHub } from '../../common/constants'
import { SiteLogo } from '../../components/customIcons'
import { SmallLoader } from '../../components/SimpleLoader/SimpleLoader'
import { salmon } from '../../styles/themeColors'
import { Error } from '../../types/graphql'

export const RedirectAutoLogin: React.FC = () => {
  const history = useHistory()
  const { auth } = useContext(AppContext)
  const [submitAutoLogin, { loading }] = useMutation(GET_USER_TOKEN)
  const [getMagicLink, { loading: magicLinkLoading }] =
    useLazyQuery(GET_MAGIC_LINK)
  const { url } = useQueryParams()
  const { token, event } = useParams<{ token: string; event: string }>()
  const dispatch = useAppDispatch()

  const [showExpiredModal, setShowExpiredModal] = useState(false)
  const [showGenericError, setShowGenericError] = useState(false)
  const [genericErrorMessage, setGenericErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState(false)
  const [linkSentSuccessfully, setLinkSentSuccessfully] = useState(false)

  const handleResendMagicLink = (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault()
    if (magicLinkLoading) return

    const form = ev.currentTarget
    const emailInput = form.elements.namedItem('email') as HTMLInputElement
    const email = emailInput.value.trim()

    if (!email) return

    getMagicLink({ variables: { event, email } })
      .then(response => {
        const error = response?.error?.message
        if (error) {
          setGenericErrorMessage(error)
          setShowGenericError(true)
          return
        }

        setSuccessMessage(true)
        setLinkSentSuccessfully(true)
      })
      .catch(err => {
        setGenericErrorMessage(
          err?.message || 'An error occurred while getting link'
        )
        setShowGenericError(true)
      })
  }

  const closeModal = () => {
    setShowExpiredModal(false)
    setShowGenericError(false)
    setSuccessMessage(false)
  }

  useEffect(() => {
    if (loading || !token) return

    submitAutoLogin({ variables: { token } })
      .then(({ data }) => {
        const errors: Error[] = data?.getUserToken?.errors || []
        if (errors.length > 0) {
          const hasExpiredError = errors.some(error =>
            error.message.toLowerCase().includes('expired')
          )

          if (hasExpiredError) {
            setShowExpiredModal(true)
            return
          }

          setGenericErrorMessage('It seems that the link is invalid.')
          setShowGenericError(true)
          return
        }

        auth.signInWithoutRequest(data.getUserToken.result)
        saveSelectedEvent(event)
        dispatch(setCurrentEventId(event))
        history.push(url || myHub)
      })
      .catch(() => {
        setGenericErrorMessage('It seems that the link is invalid.')
        setShowGenericError(true)
      })

    // no matter what, we want to call the submitAutoLogin only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {!linkSentSuccessfully ? (
        <div className="text-center py-5">redirecting...</div>
      ) : (
        <div className="text-center py-5">
          <p className="mb-3">Link sent! Check your email.</p>
          <Link to="/" className="btn btn-primary px-4">
            Go to Home
          </Link>
        </div>
      )}

      {/* Expired Link Modal */}
      <Modal
        centered
        show={showExpiredModal}
        onHide={closeModal}
        className="modal-rounded-0"
      >
        <Modal.Body className="text-center pt-4">
          <SiteLogo width="47px" height="30px" color={salmon} />
          {!successMessage ? (
            <>
              <h3 className="mt-2 text-primary">Link Expired</h3>
              <form
                onSubmit={handleResendMagicLink}
                className="px-2 px-lg-5 pb-1 pb-lg-3"
              >
                <p className="fz-14 py-2">
                  Looks like your link has expired, no worries! Just enter your
                  email below and we will send you a new link. ☺️
                </p>
                <FormControl
                  required
                  autoFocus
                  id="email"
                  type="email"
                  name="email"
                  className="mb-3"
                  autoComplete="email"
                  placeholder="Email address"
                />
                <Button
                  className="d-block px-5 mx-auto mt-3"
                  type="submit"
                  disabled={magicLinkLoading}
                >
                  {magicLinkLoading ? <SmallLoader /> : 'Resend Magic Link'}
                </Button>
              </form>
            </>
          ) : (
            <p className="fz-18 fwsb text-center mb-0 px-5 py-3 w-100">
              Magic link sent successfully!
              <br />
              Please check your email.
            </p>
          )}
        </Modal.Body>
      </Modal>

      {/* Generic Error Modal */}
      <Modal
        centered
        show={showGenericError}
        onHide={closeModal}
        className="modal-rounded-0"
      >
        <Modal.Body className="text-center pt-4">
          <SiteLogo width="47px" height="30px" color={salmon} />
          <h3 className="mt-2 text-primary">Error</h3>
          <div className="px-2 px-lg-5 pb-1 pb-lg-3">
            <p className="fz-14 py-2">{genericErrorMessage}</p>
            <Button className="d-block px-5 mx-auto mt-3" onClick={closeModal}>
              Close
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  )
}
